import { rememberedUnit } from "./previousPerformance";
import { evaluatePerformedSet } from "./performedRules";
import { effectiveExecutionProfile } from "./effectiveExecutionProfile";
import {
  appendActivityEvent,
  durationSecondsFromSegments,
  effectiveActiveSegments,
  IDLE_GAP_MINUTES,
} from "./activeDuration";

export const LOAD_PRESENTATION = Object.freeze({
  NONE: { label: null, convention: null, info: null },
  PER_IMPLEMENT: {
    label: "Weight",
    convention: "PER_IMPLEMENT",
    info: "Enter the weight of one implement.",
  },
  OLYMPIC_BARBELL: { label: "Total", convention: "TOTAL", info: "Enter the total, including the bar." },
  BAR_AS_ENTERED: {
    label: "Weight",
    convention: "AS_ENTERED",
    info: "Fixed bar: enter its printed weight. Loadable bar: enter what you loaded.",
  },
  LOADED_END: { label: "Plates", convention: "LOADED_END", info: "Enter the plates on the loaded end." },
  SMITH_PER_SIDE: {
    label: "Per side",
    convention: "PER_SIDE",
    info: "Enter the plates on one side. Smith bars vary, so Lyft Zone only compares this machine with itself.",
  },
  PLATE_LOADED: {
    label: "Plates",
    convention: "PLATES_LOADED",
    info: "Plates per side if the machine has two sides; total plates if it has one loading point.",
  },
  MACHINE_VALUE: { label: "Weight", convention: "MACHINE_VALUE", info: "Enter the value shown on the machine." },
  ADDED_WEIGHT: { label: "Added", convention: "ADDED", info: "Enter only the weight you added." },
  ASSISTANCE: { label: "Assist", convention: "ASSISTANCE", info: "Enter the assistance. Less assistance means progress." },
  OPTIONAL_WEIGHT: { label: "Weight optional", convention: "OPTIONAL_WEIGHT", info: "Leave empty if you held no weight." },
});

// `fieldUnit` is the unit the load field currently shows; it decides the bar
// wording until a load is stored.
export function loadPresentation(profile, preferences = {}, load = null, fieldUnit = null) {
  const base = LOAD_PRESENTATION[profile?.loadMode] || LOAD_PRESENTATION.NONE;
  if (profile?.loadMode === "PER_IMPLEMENT") {
    const noun = profile.entryHint === "ONE_KETTLEBELL"
      ? "kettlebell"
      : profile.entryHint === "PLATE_IMPLEMENT"
        ? "plate"
        : "dumbbell";
    return { ...base, info: `Enter the weight of one ${noun}.` };
  }
  if (profile?.loadMode === "OLYMPIC_BARBELL") {
    const hasStoredLoad = load != null;
    const convention = hasStoredLoad
      ? load.convention
      : preferences.olympicBarbellConvention;
    if (!convention) {
      return { ...base, label: "Weight", convention: null, info: null };
    }
    const perSide = convention === "PER_SIDE";
    const unit = hasStoredLoad ? load.unit : fieldUnit || preferences.weightUnit;
    const bar = hasStoredLoad
      ? load.barWeight
      : standardOlympicBarWeight(unit, preferences);
    return perSide
      ? {
          label: "Per side",
          convention: "PER_SIDE",
          info: bar
            ? `Enter the plates on one side. Lyft Zone adds the bar (${bar.value} ${unit.toLowerCase()}) when comparing progress.`
            : "Choose a weight unit before entering plates per side.",
        }
      : base;
  }
  if (profile?.loadMode === "MACHINE_VALUE") {
    if (profile.entryHint === "CABLE_ONE_STACK") {
      return { ...base, info: "Enter the value shown on one stack." };
    }
    if (profile.entryHint === "CABLE_STACK") {
      return { ...base, info: "Enter the value shown on the stack." };
    }
  }
  if (profile?.loadMode === "OPTIONAL_WEIGHT" && profile.entryHint === "PINCH_TOTAL") {
    return { ...base, info: "Enter the total plate weight." };
  }
  return base;
}

export function standardOlympicBarWeight(unit, preferences = {}) {
  if (
    preferences.olympicBarWeightValue != null &&
    preferences.olympicBarWeightUnit === unit
  ) {
    return { value: String(preferences.olympicBarWeightValue), unit };
  }
  if (unit === "KG") return { value: "20", unit };
  if (unit === "LB") return { value: "45", unit };
  return null;
}

// §12.7, in order: the previous set of this item; the per-exercise unit
// remembered from history when it is newer than the last global unit change;
// the global unit. `unitMemory` is the item's history entry and is optional.
export function defaultUnitForSet(item, set, preferences = {}, unitMemory = null) {
  const previous = (item?.sets || [])
    .filter((candidate) => candidate.setIndex < set.setIndex && candidate.load?.unit)
    .sort((left, right) => right.setIndex - left.setIndex)[0];
  return previous?.load?.unit ||
    rememberedUnit(unitMemory, preferences) ||
    preferences?.weightUnit ||
    null;
}

export function needsWeightUnit(profile) {
  return Boolean(profile && profile.loadMode !== "NONE");
}

export function needsOlympicConvention(profile) {
  return profile?.loadMode === "OLYMPIC_BARBELL";
}

// First actual when the set has no valid prescribed target (an ADDED set, for
// example): §15.4, approved amendment 2026-09-29.
export const RIR_FIRST_TAP_WITHOUT_TARGET = 2;

export function nextRirValue(actualRir, targetRir) {
  const validTarget = Number.isInteger(targetRir) && targetRir >= 0 && targetRir <= 5;
  if (actualRir == null) {
    return validTarget ? targetRir : RIR_FIRST_TAP_WITHOUT_TARGET;
  }
  if (!Number.isInteger(actualRir) || actualRir < 0 || actualRir > 5) return null;
  return actualRir === 0 ? 5 : actualRir - 1;
}

export function prescribedRepsGhost(set = {}) {
  if (set.targetReps != null) return String(set.targetReps);
  if (set.minReps != null && set.maxReps != null) return `${set.minReps}–${set.maxReps}`;
  return "";
}

export function isSetPerformed(profile, set, rirInputEnabled = true) {
  return evaluatePerformedSet({
    measurementType: profile?.measurementType,
    loadMode: profile?.loadMode,
    set,
    rirInputEnabled,
  }).performed;
}

// `performed` is the canonical performed-work total (strength/timed sets plus
// completed Cardio) that gates Save. Display surfaces use the split counts so
// Cardio is never presented as a set.
export function executionCounts(record) {
  let performedSets = 0;
  let completedCardio = 0;
  let prescribedNotPerformed = 0;
  for (const item of record?.document?.items || []) {
    const profile = effectiveExecutionProfile(record, item);
    if (profile?.measurementType === "CARDIO_MINUTES") {
      if (item.cardio?.completed && item.cardio.minutes >= 1) completedCardio += 1;
      continue;
    }
    for (const set of item.sets || []) {
      const didPerform = isSetPerformed(
        profile,
        set,
        record?.server?.policy?.rirInputEnabled !== false
      );
      if (didPerform) performedSets += 1;
      else if (set.origin === "PRESCRIBED") prescribedNotPerformed += 1;
    }
  }
  return {
    performed: performedSets + completedCardio,
    performedSets,
    completedCardio,
    prescribedNotPerformed,
  };
}

export function executionSetProgress(record) {
  let performed = 0;
  let total = 0;

  for (const item of record?.document?.items || []) {
    const profile = effectiveExecutionProfile(record, item);
    if (profile?.measurementType === "CARDIO_MINUTES") continue;
    for (const set of item.sets || []) {
      total += 1;
      if (
        isSetPerformed(
          profile,
          set,
          record?.server?.policy?.rirInputEnabled !== false
        )
      ) {
        performed += 1;
      }
    }
  }

  return { performed, total };
}

export function trackedDurationSeconds(record) {
  const baseSegments = record?.timing?.activeSegments || [];
  const segments = record?.document?.status === "ACTIVE"
    ? effectiveActiveSegments({
        activeSegments: baseSegments,
        clientStartedAt: record?.timing?.clientStartedAt,
        items: record?.document?.items || [],
      })
    : baseSegments;
  return durationSecondsFromSegments(segments);
}

// Presentation-only projection for an ACTIVE execution. Persisted base
// activeSegments remain event-driven; this only paints their still-open tail
// to `now`, then re-derives the effective Cardio union. At the canonical idle
// threshold the tail is excluded instead of manufacturing a persisted event.
export function projectedTrackedDurationSeconds(record, now = Date.now()) {
  const effectiveSeconds = trackedDurationSeconds(record);
  if (record?.document?.status !== "ACTIVE") return effectiveSeconds;

  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const lastActivityMs = new Date(record?.timing?.lastActivityAt).getTime();
  if (!Number.isFinite(nowMs) || !Number.isFinite(lastActivityMs)) {
    return effectiveSeconds;
  }

  const idleMs = nowMs - lastActivityMs;
  if (idleMs < 0 || idleMs >= IDLE_GAP_MINUTES * 60 * 1000) {
    return effectiveSeconds;
  }
  const projectedBase = appendActivityEvent({
    activeSegments: record?.timing?.activeSegments || [],
    lastActivityAt: record?.timing?.lastActivityAt,
    eventAt: new Date(nowMs).toISOString(),
  });
  return durationSecondsFromSegments(effectiveActiveSegments({
    activeSegments: projectedBase.activeSegments,
    clientStartedAt: record?.timing?.clientStartedAt,
    items: record?.document?.items || [],
  }));
}

export function formatTrackedDuration(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainingSeconds = Math.floor(safe % 60);
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`
    : `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

// Calm Review presentation of an already-frozen tracked duration.
export function formatReviewDuration(seconds) {
  const totalMinutes = Math.floor(Math.max(0, Number(seconds) || 0) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    return {
      value: `${hours}:${String(minutes).padStart(2, "0")}`,
      unit: "h",
      label: `${hours} h ${minutes} min`,
    };
  }
  return { value: String(minutes), unit: "min", label: `${minutes} min` };
}

function weekdayLabel(dateKey) {
  if (!dateKey) return null;
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: "UTC",
  }).format(new Date(`${dateKey}T12:00:00.000Z`));
}

export function executionClassification(record) {
  const projection = record?.server?.projection;
  const occurrence = record?.server?.intendedOccurrence;
  if (
    record?.server?.sourceKind === "CYCLE_OCCURRENCE" &&
    projection?.outcome === "PLANNED"
  ) {
    return `Week ${occurrence?.weekNumber ?? "—"} · ${weekdayLabel(occurrence?.plannedDate) || "Planned"}`;
  }
  const contexts = {
    FREE: "Free mode",
    CYCLE_WORKOUT: "Past cycle",
    WEEKLY_PLAN_WORKOUT: "Weekly plan",
    TRAIN_AGAIN: "Train again",
  };
  const context = contexts[record?.server?.sourceKind];
  return context ? `Extra Workout · ${context}` : "Extra Workout";
}
